import {
  Box,
  FormField,
  FormGrid,
  ModalBody,
  ModalFoot,
  ModalHead,
  SelectionList,
  Text,
} from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { MarkOptional } from 'ts-essentials';
import { v4 as uuid } from 'uuid';

import { ChatBot, ChatbotBase } from 'apis/chatbots';
import { HookFormSelectionListSingle } from 'components/hookForm';
import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import { FlowMessengerNodeChatBot } from 'components/settings/components/sections/dixaMessenger/views/flow/flow.types';
import { FlowManagerEditModalComponentT } from 'fable/flowEditor';
import { useChatBots } from 'hooks/useChatBots';
import { NotificationType, useNotifications } from 'hooks/useNotifications';

type ChatBotValue = MarkOptional<Omit<ChatbotBase, 'organizationId'>, '_type'>;
interface MessengerChatBotFormValues {
  chatbot: ChatBotValue;
}

type Props = FlowManagerEditModalComponentT<FlowMessengerNodeChatBot>;

function findChatbotById(id: string, items: ChatBot[]): ChatBot | undefined {
  return items.find((i) => i.id === id);
}

export const ChatbotModal = (props: Props): JSX.Element => {
  const { node, nodeLabel, triggerClose, replaceNode } = props;
  const { items, asyncStatus } = useChatBots();

  const chatBotName = node.payload
    ? findChatbotById(node.payload.chatbotId, items)?.name ?? ''
    : '';

  const chatbotType = node.payload
    ? findChatbotById(node.payload.chatbotId, items)?._type
    : undefined;

  const defaultValues: MessengerChatBotFormValues | undefined = node.payload
    ? {
        chatbot: {
          id: node.payload.chatbotId,
          name: chatBotName,
          _type: chatbotType,
        },
      }
    : undefined;

  const { control, formState, handleSubmit, getValues } =
    useForm<MessengerChatBotFormValues>({
      defaultValues: defaultValues,
    });
  const selectedChatbotId = getValues('chatbot')?.id || '';
  const isSyncing = (bot: ChatBot | undefined) =>
    bot?._type === 'mim' &&
    bot?.knowledgeBases?.some((kb) => kb.state === 'syncing');

  const closeModal = useCallback(triggerClose, [triggerClose]);

  const notify = useNotifications();

  const handleSaveClick = useCallback(
    (values: MessengerChatBotFormValues) => {
      if (!values.chatbot) {
        notify({ title: 'Please enter a chatbot ID' }, NotificationType.Error);
        return;
      }

      const successNextNodeId = node.payload?.successNextNodeId || uuid();

      const failNextNodeId = node.payload?.failNextNodeId || uuid();

      const nodeOptions: FlowMessengerNodeChatBot = {
        ...node,
        badges: [],
        icon: '',
        text: '',
        isAction: false,
        isEditable: true,
        isRemovable: true,
        nodeType: FlowNodeTypes.MESSENGER_CHAT_BOT,
        payload: {
          chatbotName: values.chatbot.name,
          chatbotId: values.chatbot.id,
          // mim does not support success node id
          successNextNodeId:
            values.chatbot._type == 'mim' ? undefined : successNextNodeId,
          failNextNodeId,
          additionalSettings: {
            chatbotType: values.chatbot._type,
          },
        },
        overlay: [],
      };

      replaceNode(nodeOptions);
      triggerClose();
    },
    [node, replaceNode, triggerClose, notify],
  );

  const disabledSubmit = !formState.isDirty;
  const isLoadingItems = asyncStatus === 'loading';
  const chatBotListIsEmpty = items.length === 0;
  const hasNoChatBotsDefined = chatBotListIsEmpty && !isLoadingItems;

  const getFilteredChatbots = (
    chatbots: ChatBot[],
    type: 'mim' | 'solvemate',
    title: string,
  ) => {
    const filtered = chatbots.filter((item) => item._type === type);

    if (filtered.length === 0) return null;

    return (
      <SelectionList.Section key={type} title={title}>
        {filtered.map((bot) => (
          <SelectionList.Item key={bot.id} value={bot}>
            <Text.Paragraph>{bot.name}</Text.Paragraph>
            {isSyncing(bot) && (
              <Text.Paragraph subdued variant="4">
                Knowledge base sync in process
              </Text.Paragraph>
            )}
          </SelectionList.Item>
        ))}
      </SelectionList.Section>
    );
  };

  return (
    <Box width="600px">
      <ModalHead title={nodeLabel} onClose={closeModal}>
        Choose your existing chatbot integration from the list below. When this
        node is activated, the chabot will initiate a conversation with the
        user, offering the pre-set options made on its configuration. The next
        steps must be set for each chatbot outcome.
      </ModalHead>

      <ModalBody>
        <form onSubmit={handleSubmit(handleSaveClick)}>
          {!hasNoChatBotsDefined && (
            <FormGrid>
              <FormField>
                <HookFormSelectionListSingle
                  control={control}
                  name="chatbot"
                  label="Select your chatbot integration"
                  rules={{ required: 'Chatbot is required' }}
                  componentProps={{
                    getItemTitleByValue: (
                      chatbot: ChatBotValue | null | undefined,
                    ) => {
                      const selectedChatbot = findChatbotById(
                        chatbot!.id,
                        items,
                      );
                      return (
                        <Text.Paragraph>
                          {selectedChatbot?.name ?? ''}
                        </Text.Paragraph>
                      );
                    },
                  }}
                  meta={{
                    warning:
                      selectedChatbotId &&
                      isSyncing(findChatbotById(selectedChatbotId, items))
                        ? 'The knowledge base sync is in process. It will be ready in a couple of minutes.'
                        : undefined,
                  }}
                >
                  {getFilteredChatbots(items, 'mim', 'Mim')}
                  {getFilteredChatbots(items, 'solvemate', 'Solvemate')}
                </HookFormSelectionListSingle>
              </FormField>
            </FormGrid>
          )}

          {hasNoChatBotsDefined && (
            <Text.Paragraph color={colors.orange[600]}>
              Make sure you have added a chatbot integration in the{' '}
              <Link to="/settings/integrations">settings</Link>
            </Text.Paragraph>
          )}
        </form>
      </ModalBody>

      <ModalFoot
        confirmOnClick={handleSubmit(handleSaveClick)}
        confirmName={getConfirmButtonVerb(node)}
        confirmProps={{
          disabled: disabledSubmit || isLoadingItems,
          isLoading: isLoadingItems,
        }}
        cancelOnClick={closeModal}
      />
    </Box>
  );
};
